import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import ExampleCard from "../components/ExampleCard";
import { apiFetch } from "../api";
import { PLAN_GENERATION_TYPES } from "../constants/planGenerationTypes";

export default function ExamplesPage({
                                         idea,
                                         setIdea,
                                         direction,
                                         setDirection,
                                         generationType,
                                         setGenerationType,
                                         city,
                                         setCity,
                                         showModal,
                                         setShowModal,
                                         onStart,
                                         useOwnFunds,
                                         setUseOwnFunds,
                                         ownFunds,
                                         setOwnFunds,
                                         navigate,
                                         user,
                                         onLogin,
                                         onAccount,
                                         showBlog = true,
                                         onShareSite,
                                 }) {
    const [examples, setExamples] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "СоцПлан | Примеры";
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function loadExamples() {
            try {
                const response = await apiFetch("/examples", { silent: true });
                if (!response.ok) {
                    throw new Error("Failed to load examples");
                }
                const data = await response.json();
                if (!cancelled) {
                    setExamples(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                if (!cancelled) {
                    setExamples([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadExamples();
        return () => {
            cancelled = true;
        };
    }, []);

    const pickExample = (title, presetIdea) => {
        setDirection(title);
        setIdea(presetIdea || "");
        setShowModal(true);
    };

    return (
        <div className="min-h-screen bg-slate-100 py-6 md:py-10">
            <div className="mx-auto w-full max-w-5xl rounded-2xl border bg-white shadow-md">
                <Header
                    navigate={navigate}
                    user={user}
                    onLogin={onLogin}
                    onAccount={onAccount}
                    showBlog={showBlog}
                    onShareSite={onShareSite}
                />
                <section className="px-5 md:px-6 py-6">
                    <h1 className="text-2xl font-bold mb-4">Все примеры</h1>

                    {/* Растягиваем элементы на высоту строки — карточки будут одной высоты */}
                    {loading ? (
                        <p className="mt-4 text-sm text-slate-500">Загрузка примеров...</p>
                    ) : (
                        <>
                            <div className="mt-4 grid gap-4 items-stretch sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                {examples.map((ex) => (
                                    <ExampleCard
                                        key={ex.title}
                                        title={ex.title}
                                        imagePath={ex.imagePath}
                                        onPick={() => pickExample(ex.title, ex.idea)}
                                    />
                                ))}
                            </div>
                            {examples.length === 0 && (
                                <p className="mt-4 text-sm text-slate-500">Примеры появятся позже.</p>
                            )}
                        </>
                    )}

                    <div className="mt-6 border-t pt-3 pb-5 text-[12px] text-slate-500">
                        <a href="/policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700">
                            Политика конфиденциальности
                        </a>
                        <span className="mx-2">·</span>
                        <a href="/offer" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700">
                            Оферта
                        </a>
                    </div>
                </section>
            </div>

            {showModal && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog"
                     aria-modal>
                    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
                        <div className="text-lg font-semibold">Сгенерировать бизнес-план</div>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="mb-1 block text-[13px] text-slate-700">Бизнес-направление</label>
                                <input
                                    value={direction}
                                    onChange={(e) => setDirection(e.target.value)}
                                    placeholder="Напр.: Кондитерская / Парикмахерская / Груминг"
                                    className="w-full rounded-lg border px-3 py-2 text-[15px]"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-[13px] text-slate-700">Направление генерации</label>
                                <select
                                    value={generationType}
                                    onChange={(e) => setGenerationType(e.target.value)}
                                    className="w-full rounded-lg border bg-white px-3 py-2 text-[15px]"
                                >
                                    {PLAN_GENERATION_TYPES.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                                <p className="mt-1 text-[12px] text-slate-500">
                                    Выбор сохранится в бизнес-плане и потом не редактируется.
                                </p>
                            </div>
                            <div>
                                <label className="mb-1 block text-[13px] text-slate-700">Город</label>
                                <input
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Ваш город"
                                    className="w-full rounded-lg border px-3 py-2 text-[15px]"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-[13px] text-slate-700">Опиши свою идею</label>
                                <textarea
                                    value={idea}
                                    onChange={(e) => setIdea(e.target.value)}
                                    className="w-full rounded-lg border px-3 py-2 text-[15px] min-h-[120px]"
                                />
                            </div>
                            <div>
                                <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={useOwnFunds}
                                        onChange={(e) => setUseOwnFunds(e.target.checked)}
                                        className="h-4 w-4"
                                    />
                                    Учитывать собственные средства в бизнес плане
                                </label>
                                {useOwnFunds && (
                                    <input
                                        type="number"
                                        value={ownFunds}
                                        onChange={(e) => setOwnFunds(e.target.value)}
                                        placeholder="Сумма в ₽"
                                        className="mt-2 w-full rounded-lg border px-3 py-2 text-[15px]"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="mt-4 flex items-center gap-3">
                            <button
                                onClick={onStart}
                                disabled={!idea}
                                className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50"
                            >
                                Сгенерировать
                            </button>
                            <button onClick={() => setShowModal(false)}
                                    className="h-11 rounded-lg border px-4 text-[15px]">
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
